import { useRouter } from "@tanstack/react-router";
import { useEffect } from "react";

import { fetchDeployedBuildId, isDeployedBuildStale } from "~/lib/appBuild";
import { Env } from "~/utils/Env";
import { isNavPageChange } from "~/utils/navManifest";

/** How often an open tab asks the site which build it is serving. */
export const BUILD_POLL_INTERVAL_MS = 5 * 60 * 1000;

interface UseReloadOnStaleBuildOptions {
  /**
   * Defaults to production, non-mock bundles only. Dev serves no
   * `version.json` and the Playwright preview build must never reload itself
   * mid-spec.
   */
  enabled?: boolean;
  pollIntervalMs?: number;
}

const defaultEnabled = (): boolean =>
  import.meta.env.PROD && Env.VITE_MOCK !== "true" && Env.VITE_E2E !== "true";

/**
 * Reload onto a newer deploy at the next safe moment (INS-1968). The bundle is
 * static on Cloudflare, so a tab open across a release keeps the old code until
 * it reloads. This hook polls `version.json` (on an interval and whenever the
 * tab regains focus) and, once the served build differs from the running one,
 * turns the user's next move between nav pages into a full page load of the
 * destination. Nothing is interrupted: they were leaving that page anyway, and
 * they land where they asked to go — on the current build, with access data
 * re-fetched by the boot (see useBuildChangeAccessRefresh).
 *
 * Tab switches inside a section and search-param changes never trigger it;
 * see isNavPageChange.
 */
export function useReloadOnStaleBuild(
  options: UseReloadOnStaleBuildOptions = {},
): void {
  const {
    enabled = defaultEnabled(),
    pollIntervalMs = BUILD_POLL_INTERVAL_MS,
  } = options;
  const router = useRouter();

  useEffect(() => {
    if (!enabled) return;

    let stale = false;
    let disposed = false;

    const check = async () => {
      if (stale) return;
      const deployed = await fetchDeployedBuildId();
      if (!disposed && isDeployedBuildStale(deployed)) stale = true;
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") void check();
    };

    const interval = window.setInterval(() => void check(), pollIntervalMs);
    document.addEventListener("visibilitychange", onVisibilityChange);

    const unsubscribe = router.subscribe(
      "onBeforeNavigate",
      ({ fromLocation, toLocation }) => {
        if (!stale || !fromLocation) return;
        if (!isNavPageChange(fromLocation.pathname, toLocation.pathname)) {
          return;
        }
        // A full load of the destination. The in-flight SPA navigation is
        // left to run; the unload supersedes it.
        window.location.assign(toLocation.href);
      },
    );

    return () => {
      disposed = true;
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      unsubscribe();
    };
  }, [enabled, pollIntervalMs, router]);
}
